import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";
import { DiagnosticTieIn, RelatedPatterns } from "@/components/CaseExtras";
import { useTitle } from "@/hooks/use-title";
import { CASES, YOUTUBE_URL } from "@/data/content";

function DiagnosisFlow({ diagnosis }) {
  const steps = [
    { label: "VISIBLE SYMPTOM", text: diagnosis.symptom },
    { label: "FINDING", text: diagnosis.finding },
    { label: "ROOT CAUSE", text: diagnosis.rootCause },
    { label: "RECOMMENDATION", text: diagnosis.recommendation, hot: true },
  ];
  return (
    <div className="relative mt-12 border border-line/60 bg-charcoal/30 p-7 lg:p-10" data-testid="case-diagnosis-flow">
      <div className="absolute bottom-10 left-[41px] top-24 hidden w-px bg-line/70 lg:block" />
      <motion.div
        className="absolute bottom-10 left-[41px] top-24 hidden w-px origin-top bg-glow/70 lg:block"
        initial={{ scaleY: 0 }}
        whileInView={{ scaleY: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 2, ease: EASE }}
      />
      <ol className="space-y-9">
        {steps.map((s, i) => (
          <li key={s.label} className="relative lg:pl-14" data-testid={`diagnosis-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <motion.span
              className={`absolute left-[6px] top-1 hidden h-[11px] w-[11px] rounded-full border bg-ink lg:block ${
                s.hot ? "border-glow" : "border-slate-600"
              }`}
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.3, ease: EASE }}
            >
              {s.hot && <span className="absolute inset-[3px] rounded-full bg-glow" />}
            </motion.span>
            <Reveal delay={0.2 + i * 0.2}>
              <p className={`font-mono text-[10px] uppercase tracking-[0.3em] ${s.hot ? "text-glow" : "text-slate-500"}`}>
                {s.label}
              </p>
              <p className={`mt-2 max-w-2xl text-base leading-relaxed ${s.hot ? "text-slate-100" : "text-slate-300"}`}>
                {s.text}
              </p>
            </Reveal>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function PatternCase() {
  const { slug } = useParams();
  const item = CASES.find((c) => c.slug === slug);
  useTitle(item ? `${item.title} — Operating Reality` : "Pattern not found — Operating Reality");

  if (!item) {
    return (
      <main className="wrap flex min-h-screen flex-col items-start justify-center pt-24" data-testid="case-not-found">
        <p className="kicker">Pattern not found</p>
        <h1 className="mt-6 text-4xl font-semibold tracking-tight text-slate-100">
          This reality hasn't been documented yet.
        </h1>
        <Link to="/ideas" className="btn-primary mt-10" data-testid="case-not-found-back">
          Back to the patterns
        </Link>
      </main>
    );
  }

  return (
    <main className="pt-16 lg:pt-[72px]" data-testid={`case-page-${item.slug}`}>
      <section className="relative overflow-hidden border-b border-line/50">
        <div className="bg-grid absolute inset-0 opacity-25" />
        <div className="wrap relative py-20 lg:py-28">
          <Reveal>
            <Link
              to="/ideas"
              className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-glow"
              data-testid="case-back-link"
            >
              <ArrowLeft size={13} className="transition-transform group-hover:-translate-x-1" />
              How Work Actually Works
            </Link>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <span
                className="border border-glow/50 bg-glow/5 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.28em] text-glow"
                data-testid="case-label"
              >
                {item.label}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                {item.tag}
              </span>
            </div>
            <h1 className="mt-8 max-w-4xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              {item.title}
            </h1>
          </Reveal>
        </div>
      </section>

      {item.video && (
        <section className="wrap border-b border-line/50 py-16 lg:py-24">
          <Reveal>
            <div className="mx-auto max-w-sm" data-testid="case-video-slot">
              <div className="relative aspect-[9/16] overflow-hidden border border-line/60 bg-charcoal/30">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${item.video}`}
                  title={`${item.title} — Operating Reality`}
                  className="absolute inset-0 h-full w-full"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                The short version —{" "}
                <a
                  href={YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-glow/80 transition-colors hover:text-glow"
                  data-testid="case-video-channel-link"
                >
                  Operating Reality on YouTube
                </a>
              </p>
            </div>
          </Reveal>
        </section>
      )}

      <section className="border-b border-line/50">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <p className="kicker">Case study</p>
              <p className="mt-6 text-lg leading-relaxed text-slate-300 sm:text-xl" data-testid="case-summary">
                {item.summary}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line/50 bg-charcoal/20">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <p className="kicker">Diagnosis</p>
            </Reveal>
          </div>
          <DiagnosisFlow diagnosis={item.diagnosis} />
        </div>
      </section>

      <section className="border-b border-line/50">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <p className="kicker">Try this in your organisation</p>
            </Reveal>
            <ol className="mt-10 border-b border-line/50" data-testid="case-questions">
              {item.questions.map((q, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="group flex items-baseline gap-6 border-t border-line/50 py-6">
                    <span className="font-mono text-xs tracking-[0.3em] text-glow/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-balance text-lg font-medium leading-snug tracking-tight text-slate-200 transition-colors duration-300 group-hover:text-white sm:text-xl">
                      {q}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <DiagnosticTieIn dim={item.dim} />

      <section className="bg-charcoal/20">
        <div className="wrap py-20 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="kicker">Want help?</p>
            <h2 className="mt-6 text-balance text-3xl font-semibold leading-[1.1] tracking-tight text-slate-50 sm:text-4xl">
              Recognise this pattern?
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              If this looks familiar, the next question isn't which tool to buy. It's what's
              actually happening — and why.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link to="/consulting" className="btn-primary" data-testid="case-cta-consulting">
                Explore Consulting <ArrowRight size={14} />
              </Link>
              <Link to="/contact" className="btn-ghost" data-testid="case-cta-conversation">
                Start a conversation
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <RelatedPatterns slug={item.slug} />
    </main>
  );
}
